import { CodeEditorLanguage } from "utils/interfaces";

/**
 * Function used to run the code editor content
 * @param codeFormatted
 * @param language
 */
export const runCodeEditorCode = (
  code: string,
  language: CodeEditorLanguage,
) => {
  if (language === "javascript") runJavascriptCode(code);
  if (language === "python") runPythonCode(code);
};

/**
 * Auxiliar function used to run javascript code
 * @param code
 */
const runJavascriptCode = (code: string) => {
  // Get the formatted code
  const parsedCode = `
      <script src="/p5.min.js"></script>
      <script>
        (function() {
          const serialize = (arg) => {
            if (typeof arg === 'function') {
              return 'Function: ' + arg.name;
            } else if (typeof arg === 'object') {
              try {
                return JSON.stringify(arg);
              } catch (error) {
                return 'Unserializable Object';
              }
            }
            return arg; // Return the argument directly if it's not an object or function
          };

          const originalConsole = {
            log: console.log,
            debug: console.debug,
            info: console.info,
            warn: console.warn,
            error: console.error,
            table: console.table,
            clear: console.clear,
            time: console.time,
            timeEnd: console.timeEnd,
            count: console.count,
            assert: console.assert
          };

          const overrideConsoleMethod = (methodName) => {
            console[methodName] = function(...args) {
              const serializedArgs = args.map(arg => serialize(arg));
              window.parent.postMessage({ type: 'IFRAME_CONSOLE', method: methodName, args: serializedArgs }, '*');
              // originalConsole[methodName].apply(console, args);
            };
          };

          const methodsToOverride = ['log', 'debug', 'info', 'warn', 'error', 'table', 'clear', 'time', 'timeEnd', 'count', 'assert'];
          methodsToOverride.forEach(methodName => overrideConsoleMethod(methodName));
        })();
      </script>
      <script>
      // Disable print for the current code
      function print(){}
      ${code}
      </script>
    `;

  // Clear previous output
  const outputContainer = document.getElementById("code-output");
  if (outputContainer) outputContainer.innerHTML = "";

  // And create the iframe
  if (outputContainer) {
    // Create the iframe
    const iframe = document.createElement("iframe");

    // Configure the iframe with the same previous behaviour
    iframe.id = "js-output";
    iframe.title = "sketch preview";
    iframe.allow =
      "accelerometer; autoplay; bluetooth; camera; encrypted-media; geolocation; gyroscope; hid; microphone; magnetometer; midi; payment; usb; serial; xr-spatial-tracking";
    iframe.className = `flex w-full h-full none min-w-full min-h-full relative overflow-hidden z-0 ${
      false ? "pointer-events-none" : ""
    }`;
    iframe.allowFullscreen = true;
    iframe.sandbox.add("allow-scripts"); // restrict permissions as needed
    iframe.sandbox.add("allow-forms");
    iframe.sandbox.add("allow-modals");
    iframe.sandbox.add("allow-popups");
    iframe.sandbox.add("allow-downloads");
    iframe.srcdoc = parsedCode;

    // Append the element
    outputContainer.appendChild(iframe);
  }
};

/**
 * Stop any currently running python code: cancels a running
 * animate() loop (if any) and removes the active py script tags.
 */
export const stopPythonCode = () => {
  // Execute the stop animation function
  const stopAnimation = (
    window as unknown as { __stopPythonAnimation?: () => void }
  ).__stopPythonAnimation;
  if (typeof stopAnimation === "function") stopAnimation();

  // And remove any python
  document
    .querySelectorAll('script[type="py"], script-py')
    .forEach((el) => el.remove());
};

/**
 * Auxiliary function used to run python code
 * @param code
 * @returns
 */
const runPythonCode = (code: string) => {
  // Remove any previous py script tags (and the <script-py> custom elements PyScript creates)
  document
    .querySelectorAll('script[type="py"], script-py')
    .forEach((el) => el.remove());

  // Clear previous output
  const outputContainer = document.getElementById("code-output");
  if (outputContainer) outputContainer.innerHTML = "";

  // Create a new div to show the visual outputs
  if (outputContainer) {
    // Create the div
    const div = document.createElement("div");

    div.id = "python-output";
    div.className = `flex flex-col w-full h-full none min-w-full min-h-full relative overflow-auto z-0 ${
      false ? "pointer-events-none" : ""
    }`;

    // Append the element
    outputContainer.appendChild(div);
  }

  // Import hoisting, top-level `global` detection, and the input() →
  // await input() rewrite all happen in Python (frontend/public/
  // _async_rewrite.py), via a proper AST transform rather than regexes —
  // the only reliable way to make input() work at any call depth. The raw
  // user source is handed across the JS/Python boundary via a window
  // global (mirrors window.__stopPythonAnimation in engine.py) so there's
  // no text-templating/escaping to worry about.
  (window as unknown as { __pyUserSource?: string }).__pyUserSource = code;

  // Fetch main.py boilerplate (browser caches it after first load) — it is
  // now static (no per-run templating) since main.py itself calls into
  // _async_rewrite.run_main() to build and execute the user's script.
  fetch("/main.py")
    .then((r) => r.text())
    .then((boilerplate) => {
      const pyEl = document.createElement("script");
      pyEl.type = "py";
      pyEl.textContent = boilerplate;
      document.body.appendChild(pyEl);
    });

  return () => {
    document
      .querySelectorAll('script[type="py"], script-py')
      .forEach((el) => el.remove());
  };
};
